import AddIcon from "@mui/icons-material/Add";
import CachedIcon from "@mui/icons-material/Cached";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import "react-multi-date-picker/styles/colors/green.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import {
  RATE_CARD_COLUMN_NAME,
  RATE_CARD_LABEL_NAME,
} from "./RateCardConstants";
import { getDefaultMRTOptions } from "../../dedicatedDelivery/DefaultMrtOptions";
import { RatecardResponse } from "./dtos/RateCardResponse";
import { CustomTooltip } from "../../../components/common/tooltip/CustomToolTip";
import { useSubmitModal } from "../../../components/common/modalPopup/useSubmitModal";
import AddRatecardModal from "./modals/AddRatecardModal";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import getAxiosError from "../../../app/api/axiosError";
import { getRatecardList } from "../../../app/features/ratecard/ratecardSlice";
import UpdateRatecardModal from "./modals/UpdateRatecardModal";
import agent from "../../../app/api/agent";
import ExportRatecardModal from "./modals/ExportRatecardModal";
import { mapSnakeCaseToCapitalCaseWithSpaces } from "../../../components/common/utils/StringUtils";
import { formatISODateString } from "../../../components/common/utils/DateUtils";

export type RateCardListProps = {
  clientId: number;
  clientName: string;
  clientType: string;
  startDate: Date;
  endDate: Date;
};

const RateCardList = (props: RateCardListProps) => {
  // access all parameters passed
  const clientId = props.clientId;
  const clientName = props.clientName;

  const dispatch = useAppDispatch();

  // import base configruation for grid
  const defaultMRTOptions = getDefaultMRTOptions<RatecardResponse>();

  // import store data from "Redux-Store"
  const { isLoading, ratecardList } = useSelector(
    (state: RootState) => state.ratecard
  );

  // state flag to show loader on the screen when component is exporting ratecard data
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // state flag to show loader on the screen when component is busy
  const [dataFetchig, setDataFetchig] = useState<boolean>(true);

  const [currentRatecard, setCurrentRateCard] =
    useState<RatecardResponse | null>(null);

  // ***** Callbacks *****
  const getRatecardData = useCallback(async () => {
    try {
      const response = await dispatch(getRatecardList(props.clientId)).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "ratecardList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "ratecardListException",
      });
    }
  }, [dispatch]);

  // set page title
  useEffect(() => {
    document.title = "Clients - Rate Cards";
  }, []);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRun = true;
    if (isFunctionRun) {
      getRatecardData();
    }

    return () => {
      isFunctionRun = false;
    };
  }, [getRatecardData]);

  // map columns to show on grid
  const columns = useMemo<MRT_ColumnDef<RatecardResponse>[]>(
    () => [
      {
        accessorKey: RATE_CARD_COLUMN_NAME.RATE_CARD_NAME,
        header: RATE_CARD_LABEL_NAME.RATE_CARD_NAME,
        size: 100,
      },
      {
        accessorKey: RATE_CARD_COLUMN_NAME.RATE_CARD_TYPE,
        header: RATE_CARD_LABEL_NAME.RATE_CARD_TYPE,
        size: 100,
        Cell: ({ row }) =>
          mapSnakeCaseToCapitalCaseWithSpaces(row.original.ratecardType),
      },
      {
        accessorKey: RATE_CARD_COLUMN_NAME.ASSOCIATED_STORE,
        header: RATE_CARD_LABEL_NAME.ASSOCIATED_STORE,
        size: 100,
        Cell: ({ cell, row }) =>
          row.original.ratecardClientMappings
            .map((mapping) =>
              mapping.parcelCenterName != null
                ? mapping.parcelCenterName
                : mapping.storeName
            )
            .join(", "),
      },
      {
        accessorKey: RATE_CARD_COLUMN_NAME.NUMBER_OF_INVOICES_GENERATED,
        header: RATE_CARD_LABEL_NAME.NUMBER_OF_INVOICES_GENERATED,
        size: 100,
        Cell: ({ cell, row }) => {
          if (row.original.invoiceCount) {
            return row.original.invoiceCount === null
              ? 0
              : row.original.invoiceCount;
          }
          return 0;
        },
      },
      {
        accessorKey: RATE_CARD_COLUMN_NAME.LAST_INVOICE_CREATED_DATE,
        header: RATE_CARD_LABEL_NAME.LAST_INVOICE_CREATED_DATE,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
      {
        accessorKey: RATE_CARD_COLUMN_NAME.ACTIVE,
        header: RATE_CARD_LABEL_NAME.ACTIVE,
        size: 100,
        Cell: ({ row }) => (
          <div>
            {row.original.isActive ? <span>True</span> : <span>False</span>}
          </div>
        ),
      },
      {
        accessorKey: RATE_CARD_COLUMN_NAME.CREATED_ON,
        header: RATE_CARD_LABEL_NAME.CREATED_ON,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
      {
        accessorKey: RATE_CARD_COLUMN_NAME.UPDATED_ON,
        header: RATE_CARD_LABEL_NAME.UPDATED_ON,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
    ],
    []
  );

  const handleRatecardsExport = async (ratecardIds?: string | null) => {
    try {
      setIsExporting(true);
      const response = await agent.RatecardAgent.exportRatecardData({
        clientId: clientId,
        ratecardIds: ratecardIds ?? "",
        startDate: "",
        endDate: "",
      });

      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(response);
      downloadLink.download = clientName + "-RatecardData.xlsx";
      document.body.appendChild(downloadLink);
      downloadLink.click();

      // Cleanup
      document.body.removeChild(downloadLink);
    } catch (error) {
      const appError = getAxiosError(error);

      console.error("Error downloading file:", appError.errorMessage);
      toast.error("Error downloading file:" + appError.errorMessage);
      // Handle error
    } finally {
      setIsExporting(false);
    }
  };

  // elements to be rendered on top of grid
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<RatecardResponse>
  ) => {
    return (
      <Box>
        <CustomTooltip arrow title="Refresh" onClick={getRatecardData}>
          <IconButton type="button">
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip
          arrow
          title="Add New"
          onClick={() => {
            openNewRateCardModal();
          }}
        >
          <IconButton type="button">
            <AddIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>

        <CustomTooltip
          arrow
          title="Export"
          onClick={() => {
            // if no selection made, then all rows are exported
            if (
              !table.getIsSomeRowsSelected() &&
              !table.getIsAllRowsSelected()
            ) {
              // handleRatecardsExport(null);
              openExportRateCardModal();
            } else {
              // if selection made, then all selected rows are exported
              handleRatecardsExport(
                table
                  .getSelectedRowModel()
                  .rows.map((row) => row.original.ratecardId)
                  .join(",")
              );
            }
          }}
        >
          <IconButton type="button">
            <FileUploadOutlinedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
      </Box>
    );
  };

  // open  modal logic
  const [addNewRateCardModal, openNewRateCardModal] = useSubmitModal(
    (closeNewRateCardModal) => (
      <AddRatecardModal
        clientId={clientId}
        clientType={props.clientType}
        clientName={clientName}
        closeAddRatecardModal={() => {
          closeNewRateCardModal();
          getRatecardData();
        }}
        onCompleted={() => {}}
      />
    )
  );

  // open  modal logic
  const [exportRateCardModal, openExportRateCardModal] = useSubmitModal(
    (closeNewRateCardModal) => (
      <ExportRatecardModal
        clientId={clientId}
        clientName={props.clientName}
        closeExportRatecardModal={() => {
          closeNewRateCardModal();
          getRatecardData();
        }}
        onCompleted={() => {}}
      />
    )
  );

  const [showUpdateModal, setShowUpdateModal] = useState(false);

  // store grid configuration
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: ratecardList,
    enableRowSelection: true,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {},
    },
    state: {
      showProgressBars: isLoading || isExporting,
    },
    getRowId: (row) => row.ratecardId.toString(),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        setShowUpdateModal(true);
        setCurrentRateCard(row.original);
      },
      ...defaultMRTOptions.muiTableBodyRowProps,
      sx: {
        cursor: "pointer",
        "&.MuiTableRow-root.Mui-selected td": {
          bgcolor: "#ccff90",
          color: "black",
        },
      },
    }),

    renderTopToolbarCustomActions: ({ table }) =>
      renderTopToolbarCustomActions(table),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {ratecardList.length}
        </Typography>
      );
    },
  });

  return (
    <>
      <Grid
        container
        paddingY={2}
        rowSpacing={2}
        justifyContent="space-between"
      >
        <Grid item>
          <Typography component="h2" variant="h3">
            Client: {clientName}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12} marginTop={2}>
        <MaterialReactTable table={table} />
      </Grid>
      {addNewRateCardModal}
      {showUpdateModal ? (
        <UpdateRatecardModal
          clientId={clientId}
          clientType={props.clientType}
          ratecardData={currentRatecard}
          closeUpdateRatecardModal={() => {
            getRatecardData();
            setShowUpdateModal(false);
          }}
          onCompleted={getRatecardData}
        />
      ) : null}
      {exportRateCardModal}
    </>
  );
};
export default RateCardList;
